import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BaseService } from './base.service';
import { NotificationService } from './notification.service';
import { SettingsService } from './settings.service';

import { AccountService } from 'app/account/account.service';
import { ApiResponseModel } from 'app/_models/apiResponseModel';
import { CrispTemplateEnum, getCrispTemplateStatusId } from '../_models/crisp';
import { GoogleAnalyticsChatMessage, GoogleAnalyticsEventNames } from '../_models/googleAnalytics';
import { Globals } from '../_models/globals';
import { LocalStorageUtils } from '../_utils/localstorage';
import { RolesIdsEnum } from '../_models/roles';
import { WhatsAppMessage } from '../_models/whatsAppMessage';
import { Observable } from 'rxjs';
import { isAwaitingConfirmationAfterDelivered } from 'app/_models/statusId';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { BroadCastEventEnum } from 'app/_models/_enums/broadCastEvent.enum';

declare const gtag: Function;
declare const isEmpty: any;
declare const simulateClick: any;

@Injectable()
export class GeneralService extends BaseService {
    private googleAnalyticsEventNames = new GoogleAnalyticsEventNames();
    private _commonDomain: string = '/general/common';
    private localStorageUtils: LocalStorageUtils = new LocalStorageUtils();
    public directWhatsappIntegration: boolean = false;
    public hideMultiloginDropDown = signal<boolean>(false);
    public isMercadoPartsfy: any;
    public isQuotationInsumos: any;
    public isQuotationWithoutVehicle: any;

    constructor(private accountService: AccountService, private userService: UserService, private notificationService: NotificationService, private config: SettingsService,
        private globals: Globals, private http: HttpClient, private router: Router) {
        super();

        this.directWhatsappIntegration = this.config.directWhatsappIntegration;
        this.isMercadoPartsfy = this.config.isMercadoPartsfy;
        this.isQuotationInsumos = this.config.isQuotationInsumos;
        this.isQuotationWithoutVehicle = this.config.isQuotationWithoutVehicle;
    }

    public closeQuotations() {
        this.broadCastChannel.postMessage({ tipo: BroadCastEventEnum.FecharGuiaEvent });
    }

    getPartsfyUrl(): string { return window.location.origin }

    public getBrands(): Observable<{ id: number, name: string, is_principal: boolean }[]> {
        return this.http.get<any>('/assets/json/brand.json');
    }

    public getCrispTemplate(templateId: CrispTemplateEnum) {
        return this.config.executeGet({ url: `/general/crisp/templates/${templateId}` });
    }

    public getCrispNickname() {
        let userId = this.userService.user().id;
        return this.config.executeGet({ url: `/general/crisp/get-crisp-nickname/${userId}` });
    }

    public getInsurances(): Observable<{ id: number, name: string, is_principal: boolean }[]> {
        return this.http.get<any>('/assets/json/insurance.json');
    }

    public listarRegioes() {
        return this.http.get<any>('/assets/json/region.json');
    }

    private markUseTermsAsRead() {
        this.accountService.acceptTermsOfUser().subscribe({
            next: (response: ApiResponseModel<boolean>) => {
                if (response && response.result) {
                    this.userService.setUserAcceptTermsOfUse();
                    this.notificationService.showSuccessToastr('geral.termos-uso.mensagens.sucesso.corpo', { translateMessage: true });
                }
            },
            error: (error) => {
                this.notificationService.showErrorToastr('geral.termos-uso.mensagens.erro.corpo', { translateMessage: true });
                return;
            }
        });
    }

    private sendDataToGoogleAnalytics(data: GoogleAnalyticsChatMessage) {
        gtag('event', this.googleAnalyticsEventNames.envioMensagem, {
            'type': data.analyticsType,
            'origin': data.origin,
            'sender_id': this.userService.user().id,
            'sender_role': this.userService.userIsCentral() ? 'central' : (this.userService.userIsSupport() ? 'suporte' : RolesIdsEnum[this.userService.user().role]),
            'receiver_id': data.receiverId
        });
    }

    public slack_enviar_growth() {
        let message = this.globals.translate('geral.slack.cta-v8');
        let slack_message = message.replace('{0}', this.userService.user().current_company_name);
        slack_message = slack_message.replace('{1}', this.userService.user().firstName);
        slack_message = slack_message.replace('{2}', this.userService.user().email);

        let body = { text: slack_message }

        return this.config.executePost({ url: '/general/slack/send-growth-message', body: body });
    }

    public termos_uso_montar() {
        let usuario = this.userService.user();

        if (this.router.url.indexOf('/documentos/') >= 0)
            return;

        if (!isEmpty(usuario) && !usuario.isAdmin && !this.userService.userIsCentral() && !this.userService.userIsSupport()) {
            if (!usuario.is_use_terms_accepted && !isEmpty(usuario.first_login_date)) {
                const params = this.accountService.getTermsOfUseSwal();

                this.notificationService.showConfirm(params).then((result) => {
                    if (result.isConfirmed) {
                        this.markUseTermsAsRead();
                    }
                });
            }
        }
    }

    private whatsapp_enviar(templateId: number, whatsappNumber: string, message: string[], sellerEmail: string, sellerId: number, header?: string[], isSuporte: boolean = false, quotationId: number = null) {
        let body = {
            TemplateId: templateId,
            Components: [{
                Type: 'BODY',
                Parameters: []
            }],
            ToNumber: whatsappNumber,
            IsSupport: isSuporte,
            ToEmail: sellerEmail,
            ToId: sellerId,
            QuotationId: quotationId
        };

        message.forEach((item) => { body.Components.filter(component => component.Type == 'BODY')[0].Parameters.push({ Type: 'text', Text: item }) });

        if (header) {
            body.Components.push({ Type: 'HEADER', Parameters: [] });
            header.forEach((item) => { body.Components.filter(component => component.Type == 'HEADER')[0].Parameters.push({ Type: 'text', Text: item }) });
        }

        return this.config.executePost({ url: '/general/crisp/send-whatsapp-message', body });
    }

    private whatsapp_enviar_api_web(to: number | string, msg: string = '') {
        msg = msg.replace('#', '')
        var msgUrl = msg.length > 0 ? '&text=' + encodeURI(msg) : '';

        let urlEnvio = this.config.getWhatsAppUrl() + '?phone=55' + to + msgUrl;

        simulateClick(urlEnvio, '_blank');

        return this.http.get<any>(urlEnvio);
    }

    public whatsapp_abrir_conversa(phone: string) {
        return this.whatsapp_enviar_api_web(phone);
    }

    public whatsapp_enviar_aguardando_confirmacao(message: WhatsAppMessage, nAttempts: number) {
        let whatsapp = message.vendedor_whatsapp.toString();

        if (!this.directWhatsappIntegration) {
            let template = isAwaitingConfirmationAfterDelivered(message.min_status) ? 'geral.whatsapp.aguardando-confirmacao-devolucao-troca' : 'geral.whatsapp.aguardando-confirmacao';
            let msg = this.globals.translate(template);

            msg = msg.replace('{0}', message.oficina);
            msg = msg.replace('{1}', message.veiculo);
            msg = msg.replace('{2}', message.urlCTA);

            this.whatsapp_enviar_api_web(Number(whatsapp), msg);
            return
        }

        let msg = []
        let crispTemplateEnum = getCrispTemplateStatusId(message.min_status);
        msg.push(message.oficina);
        msg.push(message.veiculo);
        msg.push(message.urlCTA);

        this.whatsapp_enviar(crispTemplateEnum, whatsapp, msg, message.vendedor_email, message.vendedor_id, undefined, message.is_from_support)
            .subscribe({
                next: (response: ApiResponseModel<GoogleAnalyticsChatMessage>) => {
                    this.sendDataToGoogleAnalytics(response.result)
                    this.notificationService.showSuccessToastr(this.globals.translate('geral.whatsapp.mensagens.sucesso.envio.corpo'));
                },
                error: (err) => {
                    let msg = this.globals.translate('geral.whatsapp.aguardando-confirmacao');
                    msg = msg.replace('{0}', message.oficina);
                    msg = msg.replace('{1}', message.veiculo);
                    msg = msg.replace('{2}', message.urlCTA);

                    this.notificationService.showErrorToastr(this.globals.translate('geral.whatsapp.mensagens.erro.envio.corpo'));

                    this.whatsapp_enviar_api_web(Number(whatsapp), msg);
                }
            });
    }

    public whatsapp_enviar_link_rapido_vendedor_externo(message: WhatsAppMessage, nAttempts: number) {
        let whatsapp = message.vendedor_whatsapp;

        let msg = this.globals.translate('geral.whatsapp.link-rapido.externo');
        msg = msg.replace('{system}', this.globals.translate('geral.sistema'));
        msg = msg.replace('{0}', message.vendedor_nome);
        msg = msg.replace('{1}', message.urlCTA);

        this.whatsapp_enviar_api_web(whatsapp, msg);
        return;
    }

    public whatsapp_enviar_link_rapido_vendedor_interno(message: WhatsAppMessage, nAttempts: number) {
        let whatsapp = message.vendedor_whatsapp;

        let msg = this.globals.translate('geral.whatsapp.link-rapido.interno');
        msg = msg.replace('{0}', message.vendedor_nome);
        msg = msg.replace('{1}', message.urlCTA);

        this.whatsapp_enviar_api_web(whatsapp, msg);
        return;
    }

    public whatsapp_enviar_responder(message: WhatsAppMessage, quotationId: number = null) {
        let whatsapp = message.vendedor_whatsapp.toString();

        if (!this.directWhatsappIntegration) {
            let msg = this.globals.translate('geral.whatsapp.nova-cotacao');
            msg = msg.replace('{0}', message.oficina);
            msg = msg.replace('{1}', message.veiculo + ' - ' + message.chassi);
            msg = msg.replace('{2}', message.urlCTA);

            this.whatsapp_enviar_api_web(Number(whatsapp), msg);
            return
        }

        let msg = [];
        msg.push(message.veiculo + ' - ' + message.chassi);
        msg.push(message.urlCTA);

        let header = [];
        header.push(message.oficina);

        this.whatsapp_enviar(CrispTemplateEnum.NovaCotacaoV2, whatsapp, msg, message.vendedor_email, message.vendedor_id, header, message.is_from_support, quotationId)
            .subscribe({
                next: (response: ApiResponseModel<GoogleAnalyticsChatMessage>) => {
                    this.sendDataToGoogleAnalytics(response.result)
                    this.notificationService.showSuccessToastr(this.globals.translate('geral.whatsapp.mensagens.sucesso.envio.corpo'));
                },
                error: (err) => {
                    let msg = this.globals.translate('geral.whatsapp.nova-cotacao');
                    msg = msg.replace('{0}', message.oficina);
                    msg = msg.replace('{1}', message.veiculo + ' - ' + message.chassi);
                    msg = msg.replace('{2}', message.urlCTA);

                    this.notificationService.showErrorToastr(this.globals.translate('geral.whatsapp.mensagens.erro.envio.corpo'));

                    this.whatsapp_enviar_api_web(Number(whatsapp), msg);
                }
            });
    }

    public whatsapp_enviar_sem_nota(message: WhatsAppMessage, data_entrega: string, texto_dia_entrega: string, tipo: string, nAttempts: number) {
        let whatsapp = message.vendedor_whatsapp;
        let msg = this.globals.translate('geral.whatsapp.sem-nota.' + tipo);
        msg = msg.replace('{0}', message.vendedor_nome);
        msg = msg.replace('{1}', data_entrega);
        msg = msg.replace('{2}', message.oficina);
        msg = msg.replace('{3}', message.veiculo);
        msg = msg.replace('{4}', message.urlCTA);

        msg = msg.replace('{5}', texto_dia_entrega.toUpperCase());

        this.whatsapp_enviar_api_web(whatsapp, msg);
        return;
    }

    public whatsapp_enviar_cobrar_nota_fiscal(message: WhatsAppMessage, texto_dia_entrega: string, tipo: string, nAttempts: number) {
        let whatsapp = message.vendedor_whatsapp.toString();

        if (!this.directWhatsappIntegration) {
            let msg = this.globals.translate('geral.whatsapp.data-entrega-proxima');
            msg = msg.replace('{0}', message.vendedor_nome);
            msg = msg.replace('{1}', texto_dia_entrega ? texto_dia_entrega.toUpperCase() : 'HOJE');
            msg = msg.replace('{2}', message.veiculo);
            msg = msg.replace('{3}', message.oficina);
            msg = msg.replace('{4}', message.urlCTA);

            this.whatsapp_enviar_api_web(Number(whatsapp), msg);
            return
        }

        let msg = [];
        msg.push(message.vendedor_nome);
        msg.push(texto_dia_entrega ? texto_dia_entrega.toUpperCase() : 'HOJE');
        msg.push(message.veiculo);
        msg.push(message.oficina);
        msg.push(message.urlCTA);

        this.whatsapp_enviar(CrispTemplateEnum.PrevisaoEntregaProxima, whatsapp, msg, message.vendedor_email, message.vendedor_id, undefined, message.is_from_support)
            .subscribe({
                next: (response: ApiResponseModel<GoogleAnalyticsChatMessage>) => {
                    this.sendDataToGoogleAnalytics(response.result)
                    this.notificationService.showSuccessToastr(this.globals.translate('geral.whatsapp.mensagens.sucesso.envio.corpo'));
                },
                error: (err) => {
                    let msg = this.globals.translate('geral.whatsapp.data-entrega-proxima');
                    msg = msg.replace('{0}', message.vendedor_nome);
                    msg = msg.replace('{1}', texto_dia_entrega ? texto_dia_entrega.toUpperCase() : 'HOJE');
                    msg = msg.replace('{2}', message.veiculo);
                    msg = msg.replace('{3}', message.oficina);
                    msg = msg.replace('{4}', message.urlCTA);

                    this.notificationService.showErrorToastr(this.globals.translate('geral.whatsapp.mensagens.erro.envio.corpo'));

                    this.whatsapp_enviar_api_web(Number(whatsapp), msg);
                }
            });
    }

    public whatsapp_enviar_atrasado(message: WhatsAppMessage, data_entrega: string, tipo: string, nAttempts: number) {
        let whatsapp = message.vendedor_whatsapp.toString();

        if (!this.directWhatsappIntegration) {
            let msg = this.globals.translate('geral.whatsapp.pedido-atrasado');
            msg = msg.replace('{0}', message.vendedor_nome);
            msg = msg.replace('{1}', data_entrega);
            msg = msg.replace('{2}', message.veiculo);
            msg = msg.replace('{3}', message.oficina);
            msg = msg.replace('{3}', message.urlCTA);

            this.whatsapp_enviar_api_web(Number(whatsapp), msg);
            return
        }

        let msg = [];
        msg.push(message.vendedor_nome);
        msg.push(data_entrega);
        msg.push(message.veiculo);
        msg.push(message.oficina);
        msg.push(message.urlCTA);

        this.whatsapp_enviar(CrispTemplateEnum.PedidoAtrasado, whatsapp, msg, message.vendedor_email, message.vendedor_id, undefined, message.is_from_support)
            .subscribe({
                next: (response: ApiResponseModel<GoogleAnalyticsChatMessage>) => {
                    this.sendDataToGoogleAnalytics(response.result)
                    this.notificationService.showSuccessToastr(this.globals.translate('geral.whatsapp.mensagens.sucesso.envio.corpo'));
                },
                error: (err) => {
                    let msg = this.globals.translate('geral.whatsapp.pedido-atrasado');
                    msg = msg.replace('{0}', message.vendedor_nome);
                    msg = msg.replace('{1}', data_entrega);
                    msg = msg.replace('{2}', message.veiculo);
                    msg = msg.replace('{3}', message.oficina);
                    msg = msg.replace('{3}', message.urlCTA);

                    this.notificationService.showErrorToastr(this.globals.translate('geral.whatsapp.mensagens.erro.envio.corpo'));

                    this.whatsapp_enviar_api_web(Number(whatsapp), msg);
                }
            });
    }

    get printPageData(): any {
        return this.localStorageUtils.obterDadosImpressao();
    }

    set printPageData(data: any) {
        this.localStorageUtils.salvarDadosImpressao(data);
    }

    public getDeliveryDateByWorkingDays(daysToAdd: number): Observable<any> {
        return this.config.executeGet({ url: `${this._commonDomain}/get-delivery-date-based-on-today/${daysToAdd}` });
    }

    public getAllDeliveryDatesByWorkingDays(days: number[]): Observable<any> {
        return this.config.executeGet({ url: `${this._commonDomain}/get-delivery-dates-based-on-today/${days.join(';')}` });
    }

    public getAllHolidays(): Observable<any> {
        return this.config.executeGet({ url: `${this._commonDomain}/get-all-holidays` });
    }
}
